import * as React from 'react';

import { enumCurrencyCode, enumUnit } from '@rematter/frontend-lib-api-client';
import { LocalReceivedItem } from '@rematter/modules/customer-portal/_state/loadReconciliation.state';
import { maybe } from '@rematter/ui-web';
import { cn, shortenUnit } from '@rematter/ui-web';
import { Button, Menu, NumberField, Tooltip } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePackageMinus, LucideTrash2 } from 'lucide-react';

import { LoadLineItemRecord } from '../../_graphql/queryLoadDetails';
import * as state from '../../_state/loadReconciliation.state';

import { LoadDeductionLine } from './LoadDeductionLine';

import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

interface LoadReceivedMaterialProps {
  receivedMaterial: LocalReceivedItem;
  lineItem: LoadLineItemRecord;
}

const useDeductionOptions = (receivedMaterial: LocalReceivedItem) => {
  const { data: deductionTypes } = useAtomValue(state.$deductionTypes);

  const addDeduction = useSetAtom(state.$addLoadDeduction);

  return React.useMemo(
    () =>
      deductionTypes?.map((deductionType) => ({
        label: deductionType.name,
        value: deductionType.id,
        onAction: () => {
          addDeduction({
            receivedLineItemId: receivedMaterial.id,
            deductionTypeId: deductionType.id,
          });
        },
      })) ?? [],
    [addDeduction, deductionTypes, receivedMaterial.id],
  );
};

export const LoadReceivedMaterial = ({
  receivedMaterial,
  lineItem,
}: LoadReceivedMaterialProps) => {
  const canEditReconciliation = useAtomValue(state.$canEditReconciliation);
  const updateReceivedMaterial = useSetAtom(state.$updateReceivedMaterial);
  const removeReceivedMaterial = useSetAtom(state.$removeReceivedMaterial);

  const deductions = useAtomValue(state.$deductionsByReceivedLineItem)[
    receivedMaterial.id
  ];

  const deductionOptions = useDeductionOptions(receivedMaterial);

  const unitPrice = maybe(receivedMaterial.unitPrice);
  const grossWeight = receivedMaterial.grossWeight?.value ?? 0;
  const tareWeight = receivedMaterial.tareWeight?.value ?? 0;
  const netWeight = grossWeight - tareWeight;
  const receivedMaterialTotal = (unitPrice?.amount ?? 0) * netWeight;
  const shortenedUnit = maybe(receivedMaterial.grossWeight?.unit, (u) =>
    shortenUnit(u),
  );

  const handleRemoveReceivedMaterial = React.useCallback(() => {
    removeReceivedMaterial({
      id: receivedMaterial.id,
      lineItemId: lineItem.id,
    });
  }, [removeReceivedMaterial, receivedMaterial.id, lineItem.id]);

  const canEditUnitPrice = React.useMemo(
    () => receivedMaterial.materialId !== lineItem.item?.id,
    [receivedMaterial.materialId, lineItem.item?.id],
  );

  const handleMaterialSelectionChange = React.useCallback(
    (value: string | undefined) => {
      updateReceivedMaterial({
        id: receivedMaterial.id,
        lineItemId: lineItem.id,
        materialId: value as string,
      });
    },
    [updateReceivedMaterial, receivedMaterial.id, lineItem.id],
  );

  const lineItemNetWeightUnit =
    lineItem.weights?.['net']?.unit ?? enumUnit.Pounds;

  const handleGrossWeightChange = React.useCallback(
    (value: number) => {
      updateReceivedMaterial({
        id: receivedMaterial.id,
        lineItemId: lineItem.id,
        grossWeight: {
          value,
          unit:
            receivedMaterial.grossWeight?.unit ??
            lineItemNetWeightUnit ??
            enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
      });
    },
    [
      updateReceivedMaterial,
      receivedMaterial.id,
      lineItem.id,
      receivedMaterial.grossWeight?.unit,
      lineItemNetWeightUnit,
    ],
  );

  const handleTareWeightChange = React.useCallback(
    (value: number) => {
      updateReceivedMaterial({
        id: receivedMaterial.id,
        lineItemId: lineItem.id,
        tareWeight: {
          value,
          unit:
            receivedMaterial.tareWeight?.unit ??
            lineItemNetWeightUnit ??
            enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
      });
    },
    [
      updateReceivedMaterial,
      receivedMaterial.id,
      lineItem.id,
      receivedMaterial.tareWeight?.unit,
      lineItemNetWeightUnit,
    ],
  );

  const handleUnitPriceChange = React.useCallback(
    (value: number) => {
      updateReceivedMaterial({
        id: receivedMaterial.id,
        lineItemId: lineItem.id,
        unitPrice: {
          amount: value,
          currencyCode: unitPrice?.currencyCode ?? enumCurrencyCode.USD,
        },
      });
    },
    [
      lineItem.id,
      receivedMaterial.id,
      unitPrice?.currencyCode,
      updateReceivedMaterial,
    ],
  );

  return (
    <>
      <div
        className={cn(
          '@LoadReceivedMaterial',
          'grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_50px_50px] items-center gap-2',
        )}
      >
        {/* 🟢🟢🟢 MATERIAL SELECTION */}
        <SelectMaterial
          isDisabled={!canEditReconciliation}
          placeholder="Select Material"
          value={receivedMaterial.materialId}
          onChange={handleMaterialSelectionChange}
        />
        {/* 🟢🟢🟢 GROSS WEIGHT */}
        <NumberField
          isDisabled={!canEditReconciliation}
          prefix={<span className="text-neutral/60">G</span>}
          suffix={shortenedUnit}
          className={'[&_input]:text-right'}
          showSteppers={false}
          value={receivedMaterial.grossWeight?.value}
          onChange={handleGrossWeightChange}
        />
        {/* 🟢🟢🟢 TARE WEIGHT */}
        <NumberField
          isDisabled={!canEditReconciliation}
          prefix={<span className="text-neutral/60">T</span>}
          suffix={shortenedUnit}
          className={'[&_input]:text-right'}
          showSteppers={false}
          value={receivedMaterial.tareWeight?.value}
          onChange={handleTareWeightChange}
        />
        {/* 🟢🟢🟢 NET WEIGHT */}
        <NumberField
          prefix={<span className="text-neutral/60">N</span>}
          isDisabled
          suffix={shortenedUnit}
          className={'[&_input]:text-right'}
          showSteppers={false}
          value={netWeight}
        />
        {/* 🟢🟢🟢 UNIT PRICE */}
        <NumberField
          isDisabled={!canEditReconciliation || !canEditUnitPrice}
          value={unitPrice?.amount ?? 0}
          suffix={`/ ${shortenedUnit}`}
          onChange={handleUnitPriceChange}
          className={'[&_input]:text-right'}
          showSteppers={false}
          formatOptions={{
            style: 'currency',
            currency: unitPrice?.currencyCode || enumCurrencyCode.USD,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        {/* 🟢🟢🟢 TOTAL VALUE */}
        <NumberField
          formatOptions={{
            style: 'currency',
            currency: unitPrice?.currencyCode || enumCurrencyCode.USD,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          value={receivedMaterialTotal}
          isDisabled
          className={'[&_input]:text-right'}
          showSteppers={false}
        />
        {/* 🟠🟠🟠 BUTTON -> ADD MATERIAL DEDUCTION */}
        <Tooltip tip="Add material deduction">
          <Menu
            trigger={
              <Button
                variant="text"
                color="neutral"
                className={'w-full'}
                isDisabled={!canEditReconciliation}
              >
                <LucidePackageMinus />
              </Button>
            }
            items={deductionOptions}
          />
        </Tooltip>
        {/* 🟠🟠🟠 BUTTON -> REMOVE MATERIAL */}
        <Button
          variant="text"
          color="failure"
          className={'w-full'}
          onClick={handleRemoveReceivedMaterial}
          isDisabled={!canEditReconciliation}
        >
          <LucideTrash2 className="text-failure" size={16} />
        </Button>
      </div>
      {deductions?.map((deduction) => (
        <LoadDeductionLine
          key={deduction.id}
          deduction={deduction}
          unit={lineItemNetWeightUnit}
          unitPrice={lineItem.unitPrice}
        />
      ))}
    </>
  );
};
